// Sound + browser-notification cues for the Order "Send for Approval" chat's
// real-time push (see backend/src/services/approvalEvents.ts) — fixed
// 2026-09-23 per direct user request ("it give sound and browser
// notification to the receiver"). Kept as a small standalone lib (not
// shared with admin/src's near-identical copy) since frontend/admin are
// separate Vite workspaces with no shared browser-side lib today (see
// admin/src/lib/'s own small, app-specific file set) — @customedge/shared
// is a pure-logic package with no DOM dependency, so it's the wrong home
// for a Notification/AudioContext helper that would break under Node.

let audioCtx: AudioContext | null = null;

// Synthesizes a short two-tone "ping" via the Web Audio API rather than
// bundling an audio file — no new binary asset to manage/host, and every
// browser that supports EventSource (which this whole feature already
// requires) also supports AudioContext. Reuses one AudioContext across
// calls (creating a fresh one per play is unnecessary and some browsers
// cap how many can exist at once).
export function playApprovalNotificationSound(): void {
  try {
    const Ctx = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    // Browsers suspend a newly-created (or backgrounded-tab) AudioContext
    // until a user gesture resumes it — best-effort resume, silently no-ops
    // if the browser still refuses (no gesture yet on this page at all,
    // e.g. the very first message of a session with zero prior clicks).
    if (audioCtx.state === 'suspended') void audioCtx.resume();

    const now = audioCtx.currentTime;
    [880, 1175].forEach((freq, i) => {
      const osc = audioCtx!.createOscillator();
      const gain = audioCtx!.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = now + i * 0.12;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.2, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
      osc.connect(gain);
      gain.connect(audioCtx!.destination);
      osc.start(start);
      osc.stop(start + 0.2);
    });
  } catch {
    // Never let a sound-playback failure break the actual chat feature.
  }
}

/**
 * Shows a browser Notification for a new approval-chat message, but only
 * when this tab is NOT the one the person is currently looking at
 * (`document.hidden`/`!document.hasFocus()`) — a message that already
 * appeared on-screen in the active tab doesn't also need an OS-level popup.
 * Silently no-ops without asking for permission; call
 * requestApprovalNotificationPermission() from a real user gesture (e.g.
 * opening the approval page) to ask once, ahead of time.
 */
export function showApprovalNotification(title: string, body: string): void {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    if (!document.hidden && document.hasFocus()) return;
    // Deliberately no shared `tag` — the DOM lib version this repo builds
    // against doesn't type `renotify`, and a `tag` WITHOUT `renotify` makes
    // some browsers silently replace an unread notification with the next
    // one instead of alerting again, which is worse than each message
    // simply getting its own separate notification.
    new Notification(title, { body });
  } catch {
    // Notification construction can throw in some embedded/iframe contexts
    // (approval pages can be opened from an email/WhatsApp link into all
    // sorts of embedding webviews) — never let this break the chat feature.
  }
}

/** Best-effort permission request — a no-op if already granted/denied or if
 *  the API/browser doesn't support it at all. Must be called from a real
 *  user gesture per the Notification API's own requirement; the caller
 *  (ApprovalPage.tsx) fires this from a click, not on mount. */
export function requestApprovalNotificationPermission(): void {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') void Notification.requestPermission();
  } catch {
    // Ignore — same rationale as showApprovalNotification above.
  }
}
